import { useState } from "react";
import { Check, Share2 } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

type ShareLinkButtonProps = {
  className?: string;
  /** Icon-only presentation for the nav bar (label shown as a title tooltip). */
  iconOnly?: boolean;
};

/**
 * Copies the current URL (which, on the explorer, already encodes the metric
 * selection and active composite weights) so a view can be shared/bookmarked.
 */
export function ShareLinkButton({ className, iconOnly = false }: ShareLinkButtonProps) {
  const [copied, setCopied] = useState(false);

  const handleShare = async () => {
    const url = globalThis.location?.href;
    if (!url) return;
    try {
      await globalThis.navigator.clipboard.writeText(url);
    } catch {
      // Clipboard unavailable (e.g. non-secure context); fall back to a prompt.
      globalThis.prompt?.("Copy the link to share this view", url);
    }
    setCopied(true);
    toast.success("Share link copied", {
      description: "Contains the current metric, weights, and pinned areas.",
    });
    globalThis.setTimeout(() => {
      setCopied(false);
    }, 2000);
  };

  return (
    <button
      type='button'
      onClick={() => {
        void handleShare();
      }}
      className={cn(
        "flex cursor-pointer items-center gap-1 rounded border border-border bg-muted/60 font-sans text-sm font-semibold text-muted-foreground transition-colors hover:bg-muted hover:text-primary",
        iconOnly ? "size-9 justify-center rounded-full" : "px-2 py-1",
        className,
      )}
      title={copied ? "Link copied" : "Copy a shareable link to this view"}>
      {copied ? <Check className='size-3.5' /> : <Share2 className='size-3.5' />}
      {!iconOnly && (copied ? "Copied" : "Share")}
    </button>
  );
}
